'use client'

import { useEffect, useRef, useState } from 'react'
import { ImagePlus, Loader2, X } from 'lucide-react'

import { BlockMini, GROUP_TONE } from '@/components/dashboard/BlockMini'
import { AVATAR_MAX_MB, AVATAR_TYPES } from '@/components/dashboard/blocks'
import Input from '../elements/Input'
import { Button } from '../elements/Button'
import { useT } from '../i18n/LocaleProvider'
import { addBlock, fetchEditorState, type BlockContent, type BlockType } from '@/lib/dashboard'
import { uploadFiles } from '@/lib/request'
import { apiError } from '@/lib/i18n/api-error'
import { formatImageURL } from '@/lib/util'
import { toast } from '@/lib/toast'
import type { PersonaSlug } from '@/lib/personas'
import type { Session } from '@/lib/types'
import { FIRST_IDEAS, readPasted, textToRich, type FirstIdea, type PreviewTile } from './persona-start'

/**
 * Sign-up's last step: the page's first block, made right here.
 *
 * One field does most of it. A pasted link is read for what it is, so a
 * Spotify track becomes a playing song, a TikTok a playing video, an Instagram
 * address a profile button, anything else a link, and nobody has to know
 * which block type that is. The persona's ideas sit under it for anyone with
 * nothing to paste: a thought to type, a photo to pick, or a hint about which
 * link to go and copy.
 *
 * Whatever is about to be added is handed up as a preview tile, so it lands
 * on the page beside the form before the button is pressed. Skippable: the
 * account is finished either way.
 */
const LINK_FIELD = 'first-block-link'

export function FirstBlock({ persona, session, onPreview, onFinish }: {
  persona: PersonaSlug
  session: Session | null
  onPreview: (_tile: PreviewTile | null) => void
  onFinish: () => void
}) {
  const t = useT()
  const ideas = FIRST_IDEAS[persona]
  const [link, setLink] = useState('')
  const [idea, setIdea] = useState<FirstIdea | null>(null)
  const [text, setText] = useState('')
  const [image, setImage] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [adding, setAdding] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const pasted = readPasted(link)
  const linkError = link.trim() && !pasted ? t('auth.firstBlock.linkInvalid') : undefined

  // What pressing Add would make right now. The idea being worked on wins;
  // otherwise whatever the link field holds.
  const pending: { type: BlockType, content: BlockContent, tile: PreviewTile } | null =
    idea?.kind === 'text' ? (text.trim() ? { type: 'text', content: { title: textToRich(text) }, tile: idea.tile } : null)
    : idea?.kind === 'image' ? (image ? { type: 'image', content: { images: [image] }, tile: idea.tile } : null)
    : pasted ? { type: pasted.type, content: pasted.content, tile: pasted.tile }
    : null

  const pendingTile = pending?.tile ?? null
  useEffect(() => { onPreview(pendingTile) }, [pendingTile, onPreview])

  const pick = (next: FirstIdea) => {
    if(idea?.id === next.id) return setIdea(null)
    setIdea(next)
    if(next.kind === 'paste') requestAnimationFrame(() => document.getElementById(LINK_FIELD)?.focus())
    if(next.kind === 'image' && !image) fileRef.current?.click()
  }

  const handleImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if(!file) return
    if(file.size > AVATAR_MAX_MB * 1024 * 1024) return toast.error(apiError(t, 'upload.too_large'))
    setUploading(true)
    const formData = new FormData()
    formData.append('file', file)
    const res = await uploadFiles({ formData })
    setUploading(false)
    if(!res) return toast.error(t('common.serverError'))
    if(res.status !== 200) return toast.error(apiError(t, res))
    const [fileName] = res.data ?? []
    if(!fileName) return toast.error(apiError(t, 'upload.failed'))
    setImage(fileName)
  }

  const add = async () => {
    if(!pending || !session) return
    setAdding(true)
    // The page was made with the handle a moment ago; its id is read rather
    // than carried through the wizard.
    const state = await fetchEditorState(session)
    if(!state.ok) {
      setAdding(false)
      return toast.error(apiError(t, state.failure))
    }
    const result = await addBlock(session, state.data.page.id, { type: pending.type, content: pending.content })
    setAdding(false)
    if(!result.ok) return toast.error(apiError(t, result.failure))
    toast.success(t('auth.firstBlock.added'))
    onFinish()
  }

  return (
    <div className="flex flex-col gap-5">
      { idea?.kind !== 'text' && idea?.kind !== 'image' && (
        <Input
          id={LINK_FIELD}
          value={link}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setLink(event.target.value)}
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => { if(event.key === 'Enter') void add() }}
          label={t('auth.firstBlock.linkLabel')}
          type="url"
          inputMode="url"
          autoCapitalize="none"
          spellCheck={false}
          placeholder={idea?.hint ?? t('auth.firstBlock.linkPlaceholder')}
          description={pasted
            ? pasted.type === 'social'
              ? t('auth.firstBlock.becomesSocial', { brand: pasted.brand ?? '' })
              : t(`auth.firstBlock.becomes.${pasted.type}`)
            : undefined}
          errorText={linkError}
          autoFocus
          container="!max-w-none"
        />
      ) }

      { idea?.kind === 'text' && (
        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-foreground">{t(`auth.firstBlock.idea.${idea.id}`)}</span>
          <textarea
            value={text}
            onChange={event => setText(event.target.value.slice(0, 1000))}
            rows={4}
            autoFocus
            placeholder={t('auth.firstBlock.textPlaceholder')}
            className="resize-none rounded-xl border border-input bg-transparent px-3.5 py-3 text-base font-medium leading-relaxed text-foreground outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40"
          />
        </label>
      ) }

      { idea?.kind === 'image' && (
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="flex size-24 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-2xl border border-dashed border-input text-muted-foreground transition-colors hover:text-foreground disabled:cursor-wait"
            aria-label={t('auth.firstBlock.pickImage')}
          >
            { uploading ? <Loader2 className="size-5 animate-spin" aria-hidden="true" />
              // eslint-disable-next-line @next/next/no-img-element
              : image ? <img src={formatImageURL(image)} alt="" className="size-full object-cover" />
              : <ImagePlus className="size-5" aria-hidden="true" /> }
          </button>
          <span className="text-sm font-medium leading-snug text-muted-foreground">
            {t(image ? 'auth.firstBlock.imageReady' : 'auth.firstBlock.pickImage')}
          </span>
        </div>
      ) }
      <input ref={fileRef} type="file" accept={AVATAR_TYPES} onChange={handleImage} className="hidden" tabIndex={-1} aria-hidden="true" />

      <div className="flex flex-col gap-2.5">
        <span className="text-[13px] font-medium text-muted-foreground">{t('auth.firstBlock.ideas')}</span>
        <div className="flex flex-wrap gap-2">
          { ideas.map(item => {
            const tone = GROUP_TONE[item.tile.group]
            const active = idea?.id === item.id
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => pick(item)}
                aria-pressed={active}
                className={`group flex cursor-pointer items-center gap-2 rounded-full border py-1 pl-1 pr-3.5 text-[13px] font-semibold outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring ${
                  active ? 'border-foreground/70 bg-card text-foreground' : 'border-border text-muted-foreground hover:border-input hover:text-foreground'
                }`}
              >
                <span
                  className="flex size-7 items-center justify-center overflow-hidden rounded-full"
                  style={{ background: `linear-gradient(135deg, ${tone.from}, ${tone.to})` }}
                  aria-hidden="true"
                >
                  <span className="scale-[.32]"><BlockMini type={item.tile.type} group={item.tile.group} /></span>
                </span>
                {t(`auth.firstBlock.idea.${item.id}`)}
                { active && <X className="size-3.5" aria-hidden="true" /> }
              </button>
            )
          }) }
        </div>
      </div>

      <div className="flex flex-col gap-2.5">
        <Button onClick={add} isActive={!!pending} isLoading={adding} width="full" loadingMessage={t('auth.firstBlock.adding')}>
          {t('auth.firstBlock.add')}
        </Button>
        <Button onClick={onFinish} variant="ghost" width="full">
          {t('auth.firstBlock.skip')}
        </Button>
      </div>
    </div>
  )
}
